// 전역 타입 선언
declare global {
  interface Window {
    $webview: {
      getMessage: (key: string, value: any) => void;
    };
  }
}

declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        webviewBridge: {
          postMessage: (message: any) => void;
        };
      };
    };
    webviewBridge?: {
      postMessage: (message: any) => void;
    };
  }
}

import { NavigateFunction } from "react-router-dom";
import { apiOption, detectOS } from "./util";
import axios from "axios";

export const isMo = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isMo =
    userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("android");
  return isMo;
};

export const isIOS = () => {
  const lowerCaseUserAgent = window.navigator.userAgent.toLowerCase();
  const isUserAgentIOS =
    lowerCaseUserAgent.indexOf("iphone") > -1 || lowerCaseUserAgent.indexOf("ipad") > -1;
  return isUserAgentIOS;
};

const registerFcm = async (fcmToken: string) => {
  const data = {
    fcm: fcmToken,
    device: detectOS(),
  };

  const response = await axios({
    ...apiOption,
    method: "POST",
    url: "/user/fcm",
    data,
  });

  return response.data;
};

// FCM 토큰 관리 유틸리티 함수들
export const fcmTokenManager = {
  // 현재 등록된 FCM 토큰 가져오기
  getCurrentToken: () => localStorage.getItem("fcmToken"),

  // 미등록 FCM 토큰 가져오기
  getUnregisteredToken: () => localStorage.getItem("unregisteredFcmToken"),

  // FCM 토큰 등록
  registerToken: async (fcmToken: string) => {
    try {
      const response = await registerFcm(fcmToken);
      if (response) {
        localStorage.setItem("fcmToken", fcmToken);
        localStorage.removeItem("unregisteredFcmToken");
        return true;
      }
      return false;
    } catch (error) {
      console.error("FCM 토큰 등록 실패:", error);
      localStorage.setItem("unregisteredFcmToken", fcmToken);
      return false;
    }
  },

  // 미등록 토큰을 서버에 등록 시도
  processUnregisteredToken: async () => {
    const unregisteredToken = fcmTokenManager.getUnregisteredToken();
    if (!unregisteredToken) return false;

    return await fcmTokenManager.registerToken(unregisteredToken);
  },

  // FCM 토큰 처리 (로그인 상태에 따라)
  processToken: async (fcmToken: string) => {
    // 이미 등록된 토큰이면 무시
    if (fcmTokenManager.getCurrentToken() === fcmToken) {
      return true;
    }

    // 로그인 상태 확인
    const isLogin = localStorage.getItem("token");

    if (isLogin) {
      // 로그인된 상태면 서버에 등록
      return await fcmTokenManager.registerToken(fcmToken);
    } else {
      // 로그인되지 않은 상태면 임시 저장
      localStorage.setItem("unregisteredFcmToken", fcmToken);
      return false;
    }
  },
};

export const getVersion = () => {
  if (typeof window === "undefined") return "";

  const userAgent = window.navigator.userAgent;

  // 3.0.0 이상 버전 체크
  const newVersionMatch = userAgent.match(/Moreden\/(\d+\.\d+\.\d+)/);
  if (newVersionMatch) {
    const version = newVersionMatch[1];
    return version;
  }

  // 3.0.0 미만 버전 체크
  const oldVersionMatch = userAgent.match(/MoredenAppVersion\/(\d+\.\d+\.\d+)/);
  if (oldVersionMatch) {
    const version = oldVersionMatch[1];
    return version;
  }

  return "";
};

export const compareVersions = (v1: string, v2: string) => {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }

  return 0;
};

export const isWebview = () => {
  if (typeof window === "undefined") return false;

  const version = getVersion();
  if (!version) return false;

  // 3.0.0 버전 기준으로 분기
  if (compareVersions(version, "3.0.0") >= 0) {
    // 3.0.0 이상 버전
    return window.navigator.userAgent.includes("Moreden/");
  } else {
    // 3.0.0 미만 버전
    return window.navigator.userAgent.includes("MoredenAppVersion");
  }
};

export function isIOSWebview() {
  return isIOS() && isWebview();
}

export const webviewPostMessage = (bridgeName: string, value: any) => {
  if (typeof window === "undefined") return;
  if (import.meta.env.VITE_APP_ARG === "dev" || import.meta.env.VITE_APP_ARG === "local") {
    console.log("bridge::", bridgeName, value);
  }
  const _isWebview = isWebview();
  const _isIOSWebview = isIOSWebview();
  const currentAppVersion = getVersion();
  if (!_isWebview) return;

  const isLatestVersion = currentAppVersion && compareVersions(currentAppVersion, "3.0.0") >= 0;

  const toOldBridgeMessage: { [key: string]: { key: string; value: any } } = {
    openSettings: {
      key: "checkNotificationPermission",
      value: "",
    },
    checkPermission: {
      key: "checkPermission",
      value: "true",
    },
    share: {
      key: "share",
      value: {
        url: value?.data?.url || "",
        title: value?.data?.title || "",
      },
    },
    "router:push": {
      key: value?.viewType === "inAppBrowser" ? "openURL" : "router:push",
      value: value?.viewType === "inAppBrowser" ? value?.url : value,
    },
    snackBar: {
      key: "alert",
      value: value || "",
    },
    dialog: {
      key: "confirm",
      value: value || "",
    },
  };

  const jsonMessage = (() => {
    if (isLatestVersion || !toOldBridgeMessage[bridgeName]) {
      return { key: bridgeName, value };
    }
    return toOldBridgeMessage[bridgeName];
  })();

  const bridge = (() => {
    // iOS 3.0.0 이상 버전부터는 iOS Native로 전환되어, window.webkit.messageHandlers.webviewBridge 를 사용합니다.
    if (_isIOSWebview && compareVersions(currentAppVersion, "3.0.0") >= 0) {
      return window?.webkit?.messageHandlers?.webviewBridge;
    }
    return window?.webviewBridge;
  })();

  if (_isWebview && typeof bridge !== "undefined") {
    bridge.postMessage(JSON.stringify(jsonMessage));
  }
};

export const getMessage = (navigate: NavigateFunction) => {
  return (key: string, value: any) => {
    console.log("getMessage::", key, value);
    switch (key) {
      case "deepLink":
      case "notification": {
        const pathname = value.split("moreden.co.kr")[1];
        const isMoreden =
          value.includes("https://moreden.co.kr") ||
          value.includes("https://www.moreden.co.kr") ||
          value.includes("https://dev.moreden.co.kr") ||
          value.includes("https://www.dev.moreden.co.kr");
        !isMoreden ? (window.location.href = value) : navigate(pathname);
        break;
      }

      case "fcmToken":
        {
          const fcmToken = value;
          fcmTokenManager.processToken(fcmToken);
        }
        break;
      default:
        break;
    }
  };
};

export const initializeBridge = (navigate: NavigateFunction) => {
  let isInitializedBridge = false;
  if (typeof window !== "undefined") {
    window.$webview = window.$webview || {};
    window.$webview.getMessage = getMessage(navigate);
  }
  if (isInitializedBridge) return;
  webviewPostMessage("initializedBridge", {});
  isInitializedBridge = true;
};

/**
 * webview 환경에서 window.open을 오버라이드하여 앱 내 브라우저로 열도록 설정
 * 이 함수는 필요할 때만 호출해야 하며, 모듈 로드 시점에 자동으로 실행되지 않습니다.
 */
export const initializeWebviewOverrides = () => {
  if (typeof window === "undefined") return;

  if (isWebview()) {
    // 기존 window.open 함수를 백업
    const originalWindowOpen = window.open;

    window.open = function (url?: string | URL): WindowProxy | null {
      if (url) {
        webviewPostMessage("router:push", {
          viewType: "inAppBrowser",
          url: url,
        });
      }
      return null;
    };

    // document.addEventListener("click", function (event) {
    //   const elem = event.target as HTMLElement;
    //   const parentAnchorElem = elem.closest("a");
    //   const urlPath = elem.getAttribute("href") || parentAnchorElem?.getAttribute("href");
    //   if (!urlPath) {
    //     return false;
    //   }

    //   if (urlPath.indexOf("http") === 0) {
    //     event.preventDefault();
    //     // http 주소 중에서
    //     if (urlPath.indexOf("moreden.co.kr") > -1) {
    //       // 플랫폼 내부 주소일 때
    //       if (
    //         urlPath.indexOf("//localhost:3000") > -1 ||
    //         urlPath.indexOf("//go.moreden.co.kr") > -1 ||
    //         urlPath.indexOf("//to.moreden.co.kr") > -1 ||
    //         urlPath.indexOf("//dl.moreden.co.kr") > -1 ||
    //         urlPath.indexOf("//dev.moreden.co.kr") > -1 ||
    //         urlPath.indexOf("//stg.moreden.co.kr") > -1 ||
    //         urlPath.indexOf("//moreden.co.kr") > -1
    //       ) {
    //         webviewPostMessage("router:push", {
    //           viewType: "bottomSheet",
    //           url: urlPath,
    //           height: 0.95,
    //           enableRefresh: false,
    //         });
    //       } else {
    //         webviewPostMessage("router:push", {
    //           viewType: "inAppBrowser",
    //           url: urlPath,
    //         });
    //       }
    //     } else {
    //       // 플랫폼 외부 주소일 때
    //       webviewPostMessage("router:push", {
    //         viewType: "inAppBrowser",
    //         url: urlPath,
    //       });
    //     }
    //   }
    // });

    // 원본 함수 복원 함수를 반환 (필요시 사용)
    return () => {
      window.open = originalWindowOpen;
    };
  }

  return null;
};

export const ui = {
  share: (data: { title: string; url: string }) => {
    try {
      webviewPostMessage("share", {
        sns: "default",
        data: {
          title: data.title,
          url: data.url,
        },
      });
    } catch (e) {
      navigator.share(data);
    }
  },
};
